import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { authFetch } from '@/lib/authFetch';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { X, Github, ExternalLink } from 'lucide-react';

// Push the site to a repository the user owns.
//
// The token is held in component state for the length of one request and is
// never persisted anywhere — not in localStorage, not on the server. Pushing
// again simply asks for it again, which is the honest trade for not storing a
// credential that can write to someone's whole account.

export default function GitHubPanel({ websiteId, defaultRepo, onClose }) {
  const [token, setToken]     = useState('');
  const [repo, setRepo]       = useState(defaultRepo || 'my-website');
  const [isPrivate, setPriv]  = useState(true);
  const [pushing, setPushing] = useState(false);
  const [result, setResult]   = useState(null);

  const push = async () => {
    if (!token.trim()) { toast.error('Paste a GitHub token first.'); return; }
    setPushing(true);
    try {
      const res = await authFetch(`/api/websites/${websiteId}/github`, {
        method: 'POST',
        body: JSON.stringify({ token: token.trim(), repo: repo.trim(), private: isPrivate }),
      });
      setResult(res);
      setToken('');   // done with it — do not leave it sitting in memory
      toast.success(`Pushed ${res.files.length} files to ${res.owner}/${res.repo}.`);
    } catch (e) { toast.error(e.message); }
    finally { setPushing(false); }
  };

  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className="absolute inset-y-0 right-0 w-80 bg-zinc-950 border-l border-zinc-800 z-20 flex flex-col shadow-2xl"
    >
      <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
        <h3 className="text-base font-bold text-white flex items-center gap-2">
          <Github className="w-4 h-4" /> Push to GitHub
        </h3>
        <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 p-5 flex flex-col gap-4 overflow-y-auto custom-scrollbar">
        {result ? (
          <>
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-3.5 py-3">
              <p className="text-emerald-300 text-sm font-medium">Pushed to {result.owner}/{result.repo}</p>
              <p className="text-emerald-400/70 text-[11px] mt-1">{result.files.length} files committed.</p>
            </div>
            <Button
              variant="outline"
              onClick={() => window.open(result.url, '_blank', 'noopener')}
              className="w-full h-10 border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800 hover:text-white rounded-xl text-sm gap-2"
            >
              <ExternalLink className="w-4 h-4" /> Open repository
            </Button>
            <Button
              variant="ghost" onClick={() => setResult(null)}
              className="w-full h-9 text-zinc-400 hover:text-white text-xs"
            >
              Push again
            </Button>
          </>
        ) : (
          <>
            <p className="text-zinc-400 text-sm leading-relaxed">
              Commits this site to a repository on your account. You keep the code — nothing about it depends on BuildersLab.
            </p>

            <div>
              <label className="text-xs text-zinc-500 mb-1.5 block">Repository name</label>
              <input
                value={repo}
                onChange={(e) => setRepo(e.target.value)}
                placeholder="my-website"
                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-xs text-zinc-100 focus:outline-none focus:border-orange-500"
              />
              <p className="text-[11px] text-zinc-600 mt-1">Created if it does not exist; updated if it does.</p>
            </div>

            <div>
              <label className="text-xs text-zinc-500 mb-1.5 block">Access token</label>
              <input
                type="password"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="ghp_… or github_pat_…"
                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-xs text-zinc-100 focus:outline-none focus:border-orange-500"
              />
              <p className="text-[11px] text-zinc-600 mt-1 leading-relaxed">
                Needs repository read and write access. Used once for this push and never stored.{' '}
                <a
                  href="https://github.com/settings/tokens?type=beta"
                  target="_blank" rel="noopener noreferrer"
                  className="text-orange-400 hover:text-orange-300"
                >
                  Create one
                </a>
              </p>
            </div>

            <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
              <input
                type="checkbox" checked={isPrivate}
                onChange={(e) => setPriv(e.target.checked)}
                className="accent-orange-500"
              />
              Make the repository private
            </label>
          </>
        )}
      </div>

      {!result && (
        <div className="p-5 border-t border-zinc-800 shrink-0">
          <Button
            onClick={push}
            disabled={pushing || !websiteId || !token.trim()}
            className="w-full h-11 bg-orange-500 hover:bg-orange-400 text-ink rounded-xl text-sm font-semibold disabled:opacity-50"
          >
            {pushing ? 'Pushing…' : 'Push to GitHub'}
          </Button>
        </div>
      )}
    </motion.div>
  );
}
